import { SoldGoodsUncategorisedListItemType } from '@shared/api/types';
import { useGetSoldUncategorisedList } from './uncategorised';

/**
 * Aggregate summary for the sold-goods Excluded items landing page — the
 * sold-goods sibling of the purchased-goods summary hook.
 *
 * Counts are real — taken from the paginated uncategorised list endpoint's
 * `count` (the full per-category total, independent of page size), fetched with
 * `page_size: '1'` so we get the total without pulling rows. The numbers are
 * whole-org / all-time: no site or time-period filters are applied.
 *
 * Spend is intentionally absent: spend isn't supported for sold goods, so the
 * landing page only ever shows item counts.
 */

/** Count for one excluded-items category. */
export interface ExcludedCategorySummary {
  /** Number of excluded items in this category. */
  count: number;
}

export interface ExcludedItemsSummary {
  /** All excluded items combined (uncertain + other). */
  total: ExcludedCategorySummary;
  /** "Items for review" — couldn't be identified, or missing weight/info. */
  uncertain: ExcludedCategorySummary;
  /** "Other goods" — identified as neither food nor packaging. */
  other: ExcludedCategorySummary;
}

export const useGetExcludedSoldItemsSummary = (): {
  data: ExcludedItemsSummary | undefined;
  isLoading: boolean;
} => {
  const uncertain = useGetSoldUncategorisedList({
    item_type: SoldGoodsUncategorisedListItemType.uncertain,
    page_size: '1',
  });
  const other = useGetSoldUncategorisedList({
    item_type: SoldGoodsUncategorisedListItemType.other,
    page_size: '1',
  });

  const isLoading = uncertain.isLoading || other.isLoading;

  if (uncertain.data == null || other.data == null) {
    return { data: undefined, isLoading };
  }

  const uncertainCount = uncertain.data.count;
  const otherCount = other.data.count;

  return {
    data: {
      uncertain: { count: uncertainCount },
      other: { count: otherCount },
      total: { count: uncertainCount + otherCount },
    },
    isLoading,
  };
};
